import type { DateKey } from "@/domain/dates";
import type { Entry, Persisted, Scenario, Subscription, UserSettings } from "@/domain/entities";

export class ChangeEmitter {
  private readonly listeners = new Set<() => void>();

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  emit(): void {
    for (const listener of [...this.listeners]) listener();
  }
}

export interface DateRange {
  from: DateKey;
  to: DateKey;
}

export interface Repository<T extends Persisted> {
  readonly changes: ChangeEmitter;
  /** Undefined for unknown ids and for tombstones. */
  get(id: string): Promise<T | undefined>;
  put(item: T): Promise<void>;
  /** Marks the row deleted and stamps updatedAt; no-op for unknown ids. */
  remove(id: string): Promise<void>;
  /** Every row, tombstones included: the raw material of an archive. */
  exportAll(): Promise<T[]>;
  /** Replaces the whole table with the given rows (a restore) and emits one change. */
  importAll(rows: T[]): Promise<void>;
}

export interface EntryRepository extends Repository<Entry> {
  /** The earliest entry of the day (daily scenarios have exactly one). */
  byDate(subscriptionId: string, date: DateKey): Promise<Entry | undefined>;
  /** All entries of the day, oldest first. */
  listByDate(subscriptionId: string, date: DateKey): Promise<Entry[]>;
  /** Sorted by date ascending. */
  listBySubscription(subscriptionId: string, range?: DateRange): Promise<Entry[]>;
  /** All subscriptions, sorted by date ascending. */
  listInRange(range: DateRange): Promise<Entry[]>;
}

export interface SubscriptionRepository extends Repository<Subscription> {
  /** Sorted by startedAt ascending. */
  listAll(): Promise<Subscription[]>;
}

export interface ScenarioRepository {
  get(id: string): Promise<Scenario | undefined>;
  list(): Promise<Scenario[]>;
}

export interface SettingsRepository {
  readonly changes: ChangeEmitter;
  get(): Promise<UserSettings>;
  update(patch: Partial<UserSettings>): Promise<UserSettings>;
}

export interface Store {
  readonly entries: EntryRepository;
  readonly subscriptions: SubscriptionRepository;
  readonly scenarios: ScenarioRepository;
  readonly settings: SettingsRepository;
}

export const DEFAULT_SETTINGS: UserSettings = { theme: "system" };

export function storeChangeSources(store: Store): ChangeEmitter[] {
  return [store.entries.changes, store.subscriptions.changes, store.settings.changes];
}
